import { getMakeupHairApiBaseUrl, getMakeupApiUrl } from '../../makeuphair/utils/apiConfig';
import {
  appendOrganizationId,
  resolveMakeupProcessImage,
} from '../../makeuphair/utils/makeupResultApi';
import { kioskAuthHeaders } from '../../makeuphair/utils/kioskAuthHeaders';
import { getFranchiseOrganizationId } from '../../shared/franchiseFlowAuth';
import { imageRefToDataUrl } from '../../shared/imageRefToDataUrl';

const FOUNDATION_DEFAULT = 0.45;

function looksBase() {
  return `${getMakeupHairApiBaseUrl().replace(/\/$/, '')}/api/looks`;
}

function orgQs() {
  const orgId = getFranchiseOrganizationId();
  if (!orgId) return '';
  return `?organization_id=${encodeURIComponent(String(orgId))}`;
}

function normalizeLooksFinish(finishType, fallback = 'matte') {
  const value = String(finishType || '')
    .toLowerCase()
    .trim();
  if (!value) return fallback;
  if (value === 'natural' || value === 'velvet') return 'matte';
  if (value === 'dewy' || value === 'cream') return 'satin';
  // Keep glossy for FLUX wet-look (do not remap to shimmer).
  if (value === 'gloss' || value === 'glossy') return 'glossy';
  return value;
}

function normalizeBlushFinish(finishType) {
  const value = String(finishType || '')
    .toLowerCase()
    .trim();
  if (!value) return 'satin';
  if (value === 'dewy') return 'sheer';
  if (value === 'natural' || value === 'velvet') return 'matte';
  if (value === 'cream') return 'satin';
  if (value === 'gloss' || value === 'glossy') return 'glossy';
  if (['matte', 'satin', 'shimmer', 'sheer', 'glossy'].includes(value)) return value;
  return 'satin';
}

function normalizeLipFinish(finishType) {
  const value = String(finishType || '')
    .toLowerCase()
    .trim();
  if (!value) return 'satin';
  if (value === 'glossy' || value === 'gloss') return 'glossy';
  if (value === 'natural' || value === 'velvet') return 'matte';
  if (value === 'dewy' || value === 'cream') return 'satin';
  if (value === 'shimmer') return 'glossy';
  if (['matte', 'satin', 'metallic', 'sheer'].includes(value)) return value;
  return 'satin';
}

/**
 * Align Looks finish payload with Makeup Studio /process expectations.
 * Blush / eyeshadow / liner / lash / brow use FLUX (no intensity scaling).
 */
export function prepareLooksLayersForApply(layers = {}) {
  const colors = { ...(layers.colors || {}) };
  const intensities = { ...(layers.intensities || {}) };
  intensities.looks_engine = intensities.looks_engine || 'flux';

  if (colors.LIP_UPPER || colors.LIP_LOWER) {
    intensities.lip_finish_type = normalizeLipFinish(intensities.lip_finish_type);
    let lip = Number(intensities.lip);
    if (!Number.isFinite(lip)) lip = 0.55;
    if (lip > 1.01) lip /= 100;
    intensities.lip = Math.max(0, Math.min(1.0, lip));
  }

  delete colors.CONCEALER;
  delete colors.LIP_LINER;
  delete intensities.concealer;
  delete intensities.lip_liner;
  delete intensities.lip_liner_finish_type;
  delete colors.HIGHLIGHTER;
  delete intensities.highlighter;
  delete intensities.highlighter_finish_type;

  if (colors.FACE) {
    let foundation = Number(intensities.foundation);
    if (!Number.isFinite(foundation)) foundation = FOUNDATION_DEFAULT;
    intensities.foundation = Math.max(0, Math.min(1, foundation));
  }

  if (colors.CHEEK_LEFT || colors.CHEEK_RIGHT) {
    delete intensities.blushSpread;
    delete intensities.blushSize;
    delete intensities.blushSmoothRadius;
    intensities.blush_finish_type = normalizeBlushFinish(intensities.blush_finish_type);
    if (!intensities.blush_position) intensities.blush_position = 'apples';
    let blush = Number(intensities.blush);
    if (Number.isFinite(blush)) {
      if (blush > 1.01) blush /= 100;
      intensities.blush = Math.max(0, Math.min(1, blush));
    }
  }

  if (colors.EYESHADOW_LEFT || colors.EYESHADOW_RIGHT) {
    delete intensities.eyeshadowSpread;
    intensities.eyeshadow_finish_type = normalizeLooksFinish(
      intensities.eyeshadow_finish_type,
      'matte',
    );
    let eyeshadow = Number(intensities.eyeshadow);
    if (Number.isFinite(eyeshadow)) {
      if (eyeshadow > 1.01) eyeshadow /= 100;
      intensities.eyeshadow = Math.max(0, Math.min(1, eyeshadow));
    }
  }

  if (colors.EYELINER_LEFT || colors.EYELINER_RIGHT) {
    let eyeliner = Number(intensities.eyeliner);
    if (!Number.isFinite(eyeliner)) eyeliner = 0.55;
    if (eyeliner > 1.01) eyeliner /= 100;
    intensities.eyeliner = Math.max(0, Math.min(1.0, eyeliner));
    intensities.eyeliner_finish_type = normalizeLooksFinish(
      intensities.eyeliner_finish_type,
      'matte',
    );
  }
  if (colors.EYELASH_LEFT || colors.EYELASH_RIGHT) {
    let eyelash = Number(intensities.eyelash);
    if (!Number.isFinite(eyelash)) eyelash = 0.55;
    if (eyelash > 1.01) eyelash /= 100;
    intensities.eyelash = Math.max(0, Math.min(1.0, eyelash));
  }
  delete intensities.eyebrow;

  return {
    ...layers,
    colors,
    intensities,
    enabled: (layers.enabled || []).filter((id) => !['concealer', 'lip-lines', 'highlighter'].includes(id)),
  };
}

export async function fetchLooksPresets() {
  const res = await fetch(`${looksBase()}/presets${orgQs()}`, {
    credentials: 'include',
    headers: kioskAuthHeaders({ Accept: 'application/json' }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.success === false) {
    throw new Error(data.error || 'Failed to load presets');
  }
  return Array.isArray(data.presets) ? data.presets : [];
}

export async function recommendLooksPresets(imageDataUrl, { timeoutMs = 45000 } = {}) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(`${looksBase()}/recommend`, {
      method: 'POST',
      credentials: 'include',
      headers: kioskAuthHeaders({ 'Content-Type': 'application/json', Accept: 'application/json' }),
      body: JSON.stringify({
        image: imageDataUrl,
        organization_id: getFranchiseOrganizationId() || undefined,
      }),
      signal: controller.signal,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.success === false) {
      throw new Error(data.error || 'Failed to recommend presets');
    }
    return {
      recommendations: Array.isArray(data.recommendations) ? data.recommendations : [],
      analysis: data.analysis || null,
    };
  } finally {
    clearTimeout(timer);
  }
}

export async function saveLooksPreset({ name, layers, description, thumbnail, tags }) {
  const res = await fetch(`${looksBase()}/presets`, {
    method: 'POST',
    credentials: 'include',
    headers: kioskAuthHeaders({ 'Content-Type': 'application/json', Accept: 'application/json' }),
    body: JSON.stringify({
      name,
      layers,
      description: description || '',
      thumbnail_url: thumbnail || null,
      tags: tags || ['custom'],
      organization_id: getFranchiseOrganizationId() || undefined,
    }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.success === false) {
    throw new Error(data.error || 'Failed to save preset');
  }
  return data.preset;
}

/**
 * After enhance, paste trimmed brow pixels from the makeup result so CodeFormer
 * cannot restore bushy brows.
 */
async function compositePreserveBrows(makeupDataUrl, enhancedDataUrl, landmarks) {
  if (!makeupDataUrl || !enhancedDataUrl || !landmarks?.length) return enhancedDataUrl;

  const load = (src) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('image load failed'));
      img.src = src;
    });

  try {
    const [makeupImg, enhImg] = await Promise.all([load(makeupDataUrl), load(enhancedDataUrl)]);
    const w = enhImg.naturalWidth || enhImg.width;
    const h = enhImg.naturalHeight || enhImg.height;
    const mw = makeupImg.naturalWidth || makeupImg.width;
    const mh = makeupImg.naturalHeight || makeupImg.height;
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return enhancedDataUrl;

    ctx.drawImage(enhImg, 0, 0, w, h);

    const maskCanvas = document.createElement('canvas');
    maskCanvas.width = w;
    maskCanvas.height = h;
    const mctx = maskCanvas.getContext('2d');
    if (!mctx) return enhancedDataUrl;

    // Dense MediaPipe brow rings + forehead fringe so excess trim is covered
    const browIndexSets = [
      [46, 53, 52, 65, 55, 70, 63, 105, 66, 107, 55, 65, 52, 53, 46],
      [276, 283, 282, 295, 285, 300, 293, 334, 296, 336, 285, 295, 282, 283, 276],
      [70, 63, 105, 66, 107, 55, 65, 52, 53, 46],
      [300, 293, 334, 296, 336, 285, 295, 282, 283, 276],
    ];
    mctx.fillStyle = '#ffffff';
    mctx.strokeStyle = '#ffffff';
    const strokeW = Math.max(8, Math.round(Math.min(w, h) * 0.018));
    mctx.lineWidth = strokeW;
    mctx.lineJoin = 'round';
    mctx.lineCap = 'round';
    browIndexSets.forEach((indices) => {
      const pts = indices
        .map((i) => landmarks[i])
        .filter((p) => p && Number.isFinite(p.x) && Number.isFinite(p.y))
        .map((p) => ({ x: p.x * w, y: p.y * h }));
      if (pts.length < 3) return;
      mctx.beginPath();
      pts.forEach((p, idx) => {
        if (idx === 0) mctx.moveTo(p.x, p.y);
        else mctx.lineTo(p.x, p.y);
      });
      mctx.closePath();
      mctx.fill();
      mctx.stroke();
    });

    // Soften via offscreen blur (drawing canvas onto itself with filter is unreliable)
    const blurCanvas = document.createElement('canvas');
    blurCanvas.width = w;
    blurCanvas.height = h;
    const bctxBlur = blurCanvas.getContext('2d');
    if (bctxBlur) {
      bctxBlur.filter = 'blur(10px)';
      bctxBlur.drawImage(maskCanvas, 0, 0);
      bctxBlur.filter = 'none';
      mctx.clearRect(0, 0, w, h);
      mctx.drawImage(blurCanvas, 0, 0);
    }

    const browCanvas = document.createElement('canvas');
    browCanvas.width = w;
    browCanvas.height = h;
    const bctx = browCanvas.getContext('2d');
    if (!bctx) return enhancedDataUrl;
    bctx.drawImage(makeupImg, 0, 0, mw, mh, 0, 0, w, h);
    bctx.globalCompositeOperation = 'destination-in';
    bctx.drawImage(maskCanvas, 0, 0);

    ctx.drawImage(browCanvas, 0, 0);
    return canvas.toDataURL('image/jpeg', 0.95);
  } catch (_) {
    return enhancedDataUrl;
  }
}

/**
 * Apply makeup (incl. brow trim). When brows are reshaped, skip CodeFormer
 * entirely — enhance before or after misaligns landmarks / restores bushy brows.
 */
export async function applyLooksLayers({ imageFile, landmarks, layers, enhance = false }) {
  const prepared = prepareLooksLayersForApply(layers);
  const intensities = prepared.intensities || {};
  const colors = prepared.colors || {};

  const browStyle = String(intensities.eyebrow_style || 'natural').toLowerCase();
  const browAdj = intensities.eyebrow_adjustments || {};
  const browTrim = Number(browAdj.trim);
  const browReshapeActive =
    (browStyle && browStyle !== 'natural') ||
    (Number.isFinite(browTrim) && browTrim > 0);

  const formData = new FormData();
  formData.append('file', imageFile);
  formData.append('colors', JSON.stringify(colors));
  formData.append('intensities', JSON.stringify(intensities));
  if (landmarks) formData.append('landmarks', JSON.stringify(landmarks));

  formData.append('lip_finish_type', intensities.lip_finish_type || 'satin');
  formData.append('blush_finish_type', intensities.blush_finish_type || 'satin');
  formData.append('eyeshadow_finish_type', intensities.eyeshadow_finish_type || 'matte');
  formData.append('eyeliner_finish_type', intensities.eyeliner_finish_type || 'matte');
  formData.append('eyebrow_style', intensities.eyebrow_style || 'natural');
  if (intensities.eyebrow_adjustments && typeof intensities.eyebrow_adjustments === 'object') {
    formData.append('eyebrow_adjustments', JSON.stringify(intensities.eyebrow_adjustments));
  }
  formData.append('blush_position', String(intensities.blush_position || 'apples'));
  if (intensities.blushSpread != null) {
    formData.append('blush_spread', String(intensities.blushSpread));
  }
  ['foundation', 'lip', 'blush', 'eyeshadow', 'eyeliner', 'eyelash', 'eyebrow'].forEach((key) => {
    if (intensities[key] != null) formData.append(key, String(intensities[key]));
  });

  if (intensities.eyeliner_template) {
    formData.append('eyeliner_template_name', String(intensities.eyeliner_template));
  }
  if (intensities.eyelash_template) {
    formData.append('eyelash_template_name', String(intensities.eyelash_template));
  }

  appendOrganizationId(formData);
  const makeupRes = await fetch(getMakeupApiUrl('/process'), {
    method: 'POST',
    credentials: 'include',
    headers: kioskAuthHeaders(),
    body: formData,
  });
  const makeupData = await makeupRes.json().catch(() => ({}));
  if (!makeupRes.ok) {
    throw new Error(makeupData.error || 'Makeup apply failed');
  }
  const resultImage = await resolveMakeupProcessImage(makeupData);
  const fluxPrompt = makeupData?.metadata?.looks_flux?.prompt || '';

  // Trim must stay final — never enhance when brows were reshaped.
  // Default: skip CodeFormer after makeup (FLUX/CV already finished); huge latency win.
  const wantsEnhance =
    enhance === true
    || String(enhance).toLowerCase() === '1'
    || String(enhance).toLowerCase() === 'force';
  if (!wantsEnhance || browReshapeActive) {
    return {
      image: resultImage,
      fluxPrompt,
      enhanced: false,
      browsPreserved: browReshapeActive,
      skippedEnhance: !wantsEnhance,
    };
  }

  // If FLUX already ran, only enhance when explicitly forced.
  const fluxUsed = Boolean(
    makeupData?.metadata?.looks_flux?.engine
    || makeupData?.metadata?.looks_flux?.layers
  );
  if (fluxUsed && String(enhance).toLowerCase() !== 'force') {
    return {
      image: resultImage,
      fluxPrompt,
      enhanced: false,
      skippedEnhance: true,
      fluxUsed: true,
    };
  }

  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 90000);
    const enhRes = await fetch(`${looksBase()}/enhance`, {
      method: 'POST',
      credentials: 'include',
      headers: kioskAuthHeaders({ 'Content-Type': 'application/json', Accept: 'application/json' }),
      body: JSON.stringify({ image: resultImage, preset: 'makeup_gentle' }),
      signal: controller.signal,
    });
    clearTimeout(timer);
    const enhData = await enhRes.json().catch(() => ({}));
    const enhImage = enhData.image || enhData.enhanced_image;
    if (enhRes.ok && enhImage) {
      return {
        image: enhImage,
        fluxPrompt,
        makeupImage: resultImage,
        enhanced: enhData.engine !== 'passthrough',
        engine: enhData.engine || 'face-enhance',
      };
    }
  } catch (_) {
    /* timeout / network — return makeup without enhance */
  }
  return { image: resultImage, enhanced: false, fluxPrompt };
}

export async function applyLooksPreset({ imageFile, landmarks, presetId, layers, enhance = false }) {
  if (layers) {
    return applyLooksLayers({ imageFile, landmarks, layers, enhance });
  }
  const res = await fetch(`${looksBase()}/apply`, {
    method: 'POST',
    credentials: 'include',
    headers: kioskAuthHeaders(),
    body: (() => {
      const fd = new FormData();
      fd.append('file', imageFile);
      fd.append('preset_id', String(presetId));
      fd.append('enhance', enhance ? '1' : '0');
      if (landmarks) fd.append('landmarks', JSON.stringify(landmarks));
      const orgId = getFranchiseOrganizationId();
      if (orgId) fd.append('organization_id', String(orgId));
      return fd;
    })(),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.success === false) {
    throw new Error(data.error || 'Apply failed');
  }
  const image = await resolveMakeupProcessImage(data);
  return {
    image,
    makeupImage: data.makeup_image || null,
    enhanced: Boolean(data.enhanced),
  };
}

/**
 * Create a short-lived download URL for QR (phone scan).
 * @param {string} imageDataUrl
 * @param {string} [name]
 */
export async function createLooksDownloadUrl(imageRef, name = 'makeup-look') {
  const imageDataUrl = await imageRefToDataUrl(imageRef);
  const res = await fetch(`${looksBase()}/download-qr`, {
    method: 'POST',
    credentials: 'include',
    headers: kioskAuthHeaders({ 'Content-Type': 'application/json', Accept: 'application/json' }),
    body: JSON.stringify({ image: imageDataUrl, name }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.success === false) {
    throw new Error(data.error || 'Failed to create download link');
  }
  const token = typeof data.token === 'string' ? data.token.trim() : '';
  if (!token) throw new Error('Download link missing');
  // Prefer absolute URL from makeup API base (works behind /naturals-makeup-api).
  return `${looksBase()}/download/${encodeURIComponent(token)}`;
}

/** Kiosk: mint a phone→kiosk upload session. */
export async function createMobileUploadSession(options = {}) {
  const branding =
    options.branding && typeof options.branding === 'object' ? options.branding : undefined;
  const res = await fetch(`${looksBase()}/mobile-upload/sessions`, {
    method: 'POST',
    credentials: 'include',
    headers: kioskAuthHeaders({ 'Content-Type': 'application/json', Accept: 'application/json' }),
    body: JSON.stringify(branding ? { branding } : {}),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.success === false) {
    throw new Error(data.error || 'Could not create phone upload link');
  }
  const token = typeof data.token === 'string' ? data.token.trim() : '';
  if (!token) throw new Error('Upload session token missing');
  return {
    token,
    pagePath: data.page_path || `/naturals/looks/mobile-upload/${token}`,
    uploadPath: data.upload_path || `/api/looks/mobile-upload/${token}`,
    expiresInSeconds: Number(data.expires_in_seconds) || 600,
  };
}

/** Build absolute phone page URL with optional theme session query params. */
export function buildMobileUploadPageUrl(pagePath, { appSessionToken = '', franchiseId = '' } = {}) {
  const path = String(pagePath || '').startsWith('/')
    ? String(pagePath)
    : `/${String(pagePath || '').replace(/^\/+/, '')}`;
  const url = new URL(path, window.location.origin);
  const sessionTok = String(appSessionToken || '').trim();
  const fid = String(franchiseId || '').trim();
  if (sessionTok) url.searchParams.set('app_session_token', sessionTok);
  if (fid) url.searchParams.set('franchise_id', fid);
  return url.toString();
}

/** Public: phone checks session status. */
export async function getMobileUploadStatus(token) {
  const res = await fetch(`${looksBase()}/mobile-upload/${encodeURIComponent(token)}`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
  });
  const data = await res.json().catch(() => ({}));
  if (res.status === 404) {
    return { ok: false, status: data.status || 'expired', branding: data.branding || null };
  }
  return {
    ok: Boolean(data.ok ?? data.success),
    status: data.status || 'expired',
    expiresInSeconds: data.expires_in_seconds,
    branding: data.branding && typeof data.branding === 'object' ? data.branding : null,
  };
}

/** Public: phone posts captured/uploaded image for this token. */
export async function postMobileUploadImage(token, imageDataUrl) {
  const res = await fetch(`${looksBase()}/mobile-upload/${encodeURIComponent(token)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ image: imageDataUrl }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.success === false) {
    throw new Error(data.error || 'Upload failed');
  }
  return data;
}

/** Kiosk: poll/claim uploaded image (one-shot when ready). */
export async function claimMobileUploadImage(token) {
  const res = await fetch(`${looksBase()}/mobile-upload/${encodeURIComponent(token)}/claim`, {
    method: 'POST',
    credentials: 'include',
    headers: kioskAuthHeaders({ Accept: 'application/json' }),
  });
  const data = await res.json().catch(() => ({}));
  if (res.ok && data.status === 'waiting') {
    return { status: 'waiting', image: null };
  }
  if (!res.ok || data.success === false) {
    if (data.status === 'waiting') return { status: 'waiting', image: null };
    throw new Error(data.error || 'Claim failed');
  }
  return {
    status: data.status || 'consumed',
    image: data.image || null,
  };
}
